import { motion } from "framer-motion";
import { ArrowDown, ArrowUp } from "lucide-react";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { resolveAvatarColor } from "@/lib/avatarColor";
import { initials, formatNumber } from "@/lib/formatters";
import { cn } from "@/lib/utils";
import type { LeaderboardBoard } from "@/api/analytics";

interface LeaderboardProps {
  boards?: LeaderboardBoard[];
  loading?: boolean;
}

const MEDALS = ["🥇", "🥈", "🥉"];

export function Leaderboard({ boards, loading }: LeaderboardProps) {
  if (loading) {
    return (
      <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-4">
        {Array.from({ length: 4 }).map((_, i) => (
          <div
            key={i}
            className="h-[220px] animate-pulse rounded-plane bg-white shadow-plane"
          />
        ))}
      </div>
    );
  }
  if (!boards || boards.length === 0) {
    return null;
  }
  return (
    <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-4">
      {boards.map((board, boardIdx) => (
        <Podium
          key={board.key ?? `board-${boardIdx}`}
          board={board}
          boardIdx={boardIdx}
        />
      ))}
    </div>
  );
}

function Podium({
  board,
  boardIdx,
}: {
  board: LeaderboardBoard;
  boardIdx: number;
}) {
  // Defensive — a malformed board (no entries field) used to crash
  // on `.slice(0, 3)`.
  const top = (board.entries ?? []).slice(0, 3);
  return (
    <motion.section
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.2, delay: boardIdx * 0.06 }}
      className="rounded-plane bg-white p-4 shadow-plane"
    >
      <h3 className="mb-3 flex items-center gap-2 text-sm font-semibold text-slate-800">
        <span aria-hidden>{board.emoji}</span>
        {board.title}
      </h3>
      {top.length === 0 ? (
        <p className="text-xs italic text-slate-400">No data.</p>
      ) : (
        <ol className="space-y-3">
          {top.map((entry, i) => {
            const avatar = resolveAvatarColor(
              entry.avatar_color,
              entry.member_id,
            );
            // The medal scale-in mirrors a real podium reveal — small
            // touch but it makes the section feel rewarding for the
            // top performer.
            return (
              <motion.li
                // member_id can be missing for engine / aggregate
                // rows; pair with the row index for uniqueness.
                key={`${entry.member_id ?? "anon"}-${i}`}
                initial={{ opacity: 0, x: -4 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{
                  duration: 0.2,
                  delay: boardIdx * 0.06 + i * 0.06 + 0.1,
                }}
                className="flex items-start gap-2"
              >
                <motion.span
                  aria-hidden
                  initial={{ scale: 1.2 }}
                  animate={{ scale: 1 }}
                  transition={{
                    duration: 0.2,
                    delay: boardIdx * 0.06 + i * 0.06 + 0.15,
                  }}
                  className="text-lg leading-none"
                >
                  {MEDALS[i]}
                </motion.span>
                <Avatar className="mt-0.5 h-5 w-5 border border-slate-200">
                  <AvatarFallback
                    className={cn(
                      "text-[9px] font-semibold",
                      avatar.bg,
                      avatar.text,
                    )}
                  >
                    {initials(entry.member_name)}
                  </AvatarFallback>
                </Avatar>
                <div className="min-w-0 flex-1">
                  <div className="truncate text-[13px] font-medium text-slate-800">
                    {entry.member_name}
                  </div>
                  <div className="flex items-baseline gap-2 text-xs">
                    <span className="font-semibold tabular-nums text-slate-900">
                      {formatNumber(entry.primary_value)}
                    </span>
                    {entry.delta != null && Number.isFinite(entry.delta) && (
                      <DeltaInline delta={entry.delta} />
                    )}
                  </div>
                  {entry.secondary_label && (
                    <div className="text-[11px] text-slate-500">
                      {entry.secondary_label}
                    </div>
                  )}
                </div>
              </motion.li>
            );
          })}
        </ol>
      )}
      {board.subtitle && (
        <p className="mt-3 text-[10px] uppercase tracking-wider text-slate-400">
          {board.subtitle}
        </p>
      )}
    </motion.section>
  );
}

function DeltaInline({ delta }: { delta: number }) {
  const rounded = Math.round(delta * 10) / 10;
  const positive = rounded > 0;
  const negative = rounded < 0;
  const Icon = positive ? ArrowUp : negative ? ArrowDown : null;
  return (
    <span
      className={cn(
        "inline-flex items-center gap-0.5 text-[10px] font-semibold tabular-nums",
        positive && "text-emerald-600",
        negative && "text-rose-600",
        !positive && !negative && "text-slate-400",
      )}
    >
      {Icon && <Icon className="h-2.5 w-2.5" />}
      {Math.abs(rounded)}
    </span>
  );
}
